import React, { useState, useEffect, useRef } from "react";
import BreadCrumbBanner from "../component/BreadCrumbBanner";
import { Link } from "react-router-dom";
import AlumniFormModal from "./AlumniFormModal";
import LoadMoreButton from "../component/LoadMoreButton";
import aboutAPI from "../api/aboutApi";
import { Youtube, ExternalLink } from "lucide-react";

const Alumni = () => {
  const [visibleCards, setVisibleCards] = useState(6);
  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(true);
  const loadMoreRef = useRef(null);

  const loadMore = () => {
    setVisibleCards((prev) => prev + 6);
  };

  useEffect(() => {
    fetchAlumniDetails();
  }, []);

  useEffect(() => {
    if (loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && visibleCards < alumni.length) {
          loadMore();
        }
      },
      { threshold: 1 },
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [loading, visibleCards, alumni.length]);

  const fetchAlumniDetails = async () => {
    try {
      setLoading(true);
      const res = await aboutAPI.alumni();
      if (res.code === 200) {
        setAlumni(res.data.alumni || []);
      } else {
        setAlumni([]);
      }
    } catch (error) {
      console.error(error);
      setAlumni([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* BreadCrump Section */}
      <BreadCrumbBanner
        title="Alumni"
        backgroundImage={`${process.env.PUBLIC_URL}/assets/img/about-top2.webp`}
      />

      <section className="about-mrv-alumni-section">
        <div className="container">
          {/* Heading */}
          <div className="row justify-content-between align-items-center mb-3">
            <div className="col-8">
              <h2 className="mrv-title">Our Alumni Network</h2>
            </div>

            <div className="col-4 text-end">
              <Link
                to="#"
                className="mrv-btn-outline text-decoration-none"
                data-bs-toggle="modal"
                data-bs-target="#alumniModal"
              >
                Join Our Alumni Network
              </Link>
            </div>
          </div>
          <div className="row g-4">
            {loading ? (
              <div className="text-center py-5">
                <p>Loading...</p>
              </div>
            ) : alumni.length > 0 ? (
              alumni.slice(0, visibleCards).map((item, index) => (
                <div key={item.id || index} className="col-lg-4 col-md-6 mt-5">
                  <div className="about-mrv-alumni-card">
                    <div className="about-mrv-alumni-video-box">
                      <img
                        src={item.thumbnail}
                        className="about-mrv-alumni-thumbnail"
                        alt={item.name}
                        loading="lazy"
                      />
                    </div>

                    <div className="about-mrv-alumni-content">
                      <h4 className="about-mrv-alumni-name">{item.name}</h4>
                      <span className="about-mrv-alumni-batch">
                        {item.title}
                      </span>

                     {item?.video_url && (
                       <a
                        href={item?.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="custom-youtube-btn"
                      >
                        <span className="custom-youtube-icon">
                          <i className="bi bi-youtube"></i>
                        </span>

                        <span>Watch Intro Video on YouTube</span>

                        <ExternalLink size={18} />
                      </a>
                     )}
                      <p dangerouslySetInnerHTML={{ __html: item.bio }} />
                      <div className="about-mrv-alumni-position">
                         Batch {item.batch}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-5">
                <p>No alumni found.</p>
              </div>
            )}
          </div>

          {/* Sentinel for infinite scroll */}
          {!loading && visibleCards < alumni.length && (
            <div ref={loadMoreRef} style={{ height: "1px" }} />
          )}
        </div>
      </section>

      {/* alumni form */}
      <div className="modal fade" id="alumniModal" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header d-flex justify-content-between">
              <h5 className="modal-title">Join Our Alumni Network</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              ></button>
            </div>

            <div className="modal-body">
              <AlumniFormModal onSuccess={fetchAlumniDetails} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Alumni;
